import Segmented from 'antd/lib/segmented';
import * as echarts from 'echarts/core';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import { LineChart } from 'echarts/charts';
import { DataZoomComponent, GridComponent, LegendComponent, MarkAreaComponent, TooltipComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { ECharts, EChartsOption } from 'echarts';
import { TopLevelFormatterParams } from 'echarts/types/dist/shared';
import { useMemo, useRef, useState } from 'react';
import Space from 'antd/lib/space';
import Typography from 'antd/lib/typography';
import { Divider, Tooltip } from 'antd';
import { theme } from 'antd/lib';
import Row from 'antd/lib/row';
import Col from 'antd/lib/col';
import Empty from 'antd/lib/empty';
import { orderBy } from 'lodash';

import { ReactComponent as MoneyIcon } from '../icons/money.svg';
import { CHART_DATA_COLOR } from '../../pages/peak-load-forecast/Charts/chart';
import { IPriceResponseTriggerWithPricePeriods, PRICE_TYPE_TEXT_MAP, PriceType } from '../../domain/price-response/interface';
import { extractPriceSeries } from '../../pages/priceResponse/view/scenarios/calculatePriceResponseScenariosEvents';
import { PriceResponseMarket } from '../../pages/priceResponse/view/scenarios/PriceResponseMarket';
import { getTimezoneFormattedTime } from '../../domain/date/date';

const CHART_GROUP_ID = 'price-response-analytic-charts';

echarts.connect(CHART_GROUP_ID);

echarts.use([LineChart, TooltipComponent, LegendComponent, GridComponent, CanvasRenderer, MarkAreaComponent, DataZoomComponent]);

const priceTypeOptions = [
    {
        label: PRICE_TYPE_TEXT_MAP[PriceType.REAL_TIME],
        value: PriceType.REAL_TIME,
    },
    {
        label: PRICE_TYPE_TEXT_MAP[PriceType.DAY_AHEAD],
        value: PriceType.DAY_AHEAD,
    },
];

interface ITriggerPricePeriod {
    start: number; // timestamp (ms)
    end: number; // timestamp (ms)
    price: number;
}

interface ITriggerPrice {
    id: string;
    priceType: PriceType;
    color?: string;
    periods: ITriggerPricePeriod[];
}

const DEFAULT_TRIGGER_COLORS = ['#ff4d4f', '#faad14', '#722ed1', '#13c2c2', '#eb2f96', '#52c41a'];

const getTriggerColor = (trigger: ITriggerPrice, index: number): string =>
    trigger.color ?? DEFAULT_TRIGGER_COLORS[index % DEFAULT_TRIGGER_COLORS.length];

const getLatestTriggerPrice = (trigger: ITriggerPrice): number | undefined => {
    return orderBy(trigger.periods, 'end', 'desc').find(period => period.price !== undefined)?.price;
};

const getTriggerLabel = (trigger: ITriggerPrice): string => {
    return `Trigger (Id: ${trigger.id})`;
};

const seriesName = (priceType: PriceType) => `${PRICE_TYPE_TEXT_MAP[priceType]} Price`;

const mapTriggersToChartData = (triggers: IPriceResponseTriggerWithPricePeriods[], selectedPriceType: PriceType): ITriggerPrice[] =>
    triggers
        .filter(
            trigger => (trigger?.trigger?.price_type ? trigger.trigger.price_type === selectedPriceType : true) && trigger.pricePeriods.length > 0
        )
        .map(trigger => ({
            id: String(trigger.id),
            priceType: trigger.trigger?.price_type || selectedPriceType,
            periods: orderBy(
                trigger.pricePeriods.map(period => ({
                    start: new Date(period.start).getTime(),
                    end: new Date(period.end).getTime(),
                    price: period.price,
                })),
                'start'
            ),
        }));

const buildSteppedSeriesData = (periods: ITriggerPricePeriod[], chartData: [number, number][]): [number, number | null][] =>
    chartData
        .map(([timestamp]) => {
            const period = periods.find(period => timestamp >= period.start && timestamp <= period.end);

            return [timestamp, period ? period.price : null];
        })
        .filter(([, price]) => price !== null) as [number, number | null][];

// --- Tooltip -----------------------------------------------------------

const formatTooltip = (params: TopLevelFormatterParams, market: PriceResponseMarket): string => {
    if (!Array.isArray(params) || !params.length) {
        return '';
    }

    const firstParam = params[0];

    if (!Array.isArray(firstParam.value) || typeof firstParam.value[0] !== 'number') {
        return '';
    }

    const timestamp = firstParam.value[0];
    const dateStr = getTimezoneFormattedTime(new Date(timestamp), market.timezone);

    const rows = params
        .filter(param => Array.isArray(param.value) && (param.value as (number | null)[])[1] != null)
        .map(param => {
            const price = (param.value as [number, number])[1];

            return `
                <div>
                    ${param.marker}
                    ${param.seriesName}:
                    <strong>$${price.toFixed(2)}</strong>
                </div>
            `;
        })
        .join('');

    return `
        <div style="padding: 6px 10px;">
            <div style="color: #697475; margin-bottom: 4px;">${dateStr}</div>
            ${rows}
        </div>
    `;
};

// --- Component ---------------------------------------------------------

interface IPriceResponseScenariosChartProps {
    prices: [number, number, number][];
    triggers: IPriceResponseTriggerWithPricePeriods[];
    market: PriceResponseMarket;
    isLoading: boolean;
    start?: Date;
    end?: Date;
}

export const PricesWidget = ({ prices, triggers, market, isLoading, start, end }: IPriceResponseScenariosChartProps) => {
    const { token: themeColors } = theme.useToken();

    const [selectedPriceType, setSelectedPriceType] = useState<PriceType>(PriceType.REAL_TIME);

    const realTimeChartSeries = useMemo(() => {
        if (!prices.length) return [];
        return extractPriceSeries(prices, PriceType.REAL_TIME);
    }, [prices]);

    const dayAheadChartSeries = useMemo(() => {
        if (!prices.length) return [];
        return extractPriceSeries(prices, PriceType.DAY_AHEAD);
    }, [prices]);

    const chartData = (selectedPriceType === PriceType.REAL_TIME ? realTimeChartSeries : dayAheadChartSeries).map(point => [
        point.timestamp,
        point.price,
    ]);

    const isNoData = chartData.length === 0;

    const chartTriggers = useMemo(() => mapTriggersToChartData(triggers, selectedPriceType), [triggers, selectedPriceType]);

    const steppedSeries = useMemo(
        () =>
            chartTriggers.map((trigger, index) => {
                const triggerColor = getTriggerColor(trigger, index);
                const triggerLabel = getTriggerLabel(trigger);

                return {
                    name: triggerLabel,
                    type: 'line' as const,
                    step: 'end' as const,
                    data: buildSteppedSeriesData(trigger.periods, chartData as [number, number][]),
                    showSymbol: false,
                    lineStyle: {
                        color: triggerColor,
                        type: 'dashed' as const,
                        width: 2,
                    },
                    itemStyle: {
                        color: triggerColor,
                    },
                    endLabel: {
                        show: true,
                        formatter: () => `Trigger $${getLatestTriggerPrice(trigger)}`,
                        color: triggerColor,
                        offset: [-10, -10],
                        align: 'right' as const,
                    },
                };
            }),
        [chartTriggers]
    );

    const chartInstanceRef = useRef<ECharts | null>(null);

    const option: EChartsOption = {
        grid: {
            top: '40px',
            left: '80px',
            right: '40px',
            bottom: '60px',
        },
        legend: {
            top: 0,
            data: [seriesName(selectedPriceType)],
            selectedMode: false,
        },
        tooltip: {
            trigger: 'axis',
            padding: 0,
            borderColor: '#ffffff',
            axisPointer: {
                type: 'line',
                lineStyle: { type: 'dashed' },
            },
            formatter: params => formatTooltip(params, market),
            position: (point, params, dom, rect, size) => {
                const paramsArr = Array.isArray(params) ? params : [params];
                const axisValue = (paramsArr[0] as any)?.axisValue;

                const instance = chartInstanceRef.current;

                let x = point[0];
                if (instance && axisValue != null) {
                    const px = instance.convertToPixel({ xAxisIndex: 0 }, axisValue);
                    if (typeof px === 'number' && !Number.isNaN(px)) {
                        x = px;
                    }
                }

                const { contentSize, viewSize } = size;
                let posX = x + 15;
                let posY = viewSize[1] / 2 - contentSize[1] / 2;

                if (posX + contentSize[0] > viewSize[0]) posX = x - contentSize[0] - 15;
                if (posY < 0) posY = 0;
                if (posY + contentSize[1] > viewSize[1]) posY = viewSize[1] - contentSize[1];

                return [posX, posY];
            },
        },
        xAxis: {
            type: 'time',
            min: start && start.getTime(),
            max: end && end.getTime(),
            axisLabel: {
                color: '#697475',
                formatter: (value: number) => getTimezoneFormattedTime(new Date(value), market.timezone),
                hideOverlap: true,
            },
            axisLine: {
                lineStyle: { color: '#d6dbdb', width: 2 },
            },
        },
        yAxis: {
            type: 'value',
            axisLabel: {
                color: '#697475',
                formatter: (value: number) => `$${value}`,
            },
            splitLine: { show: true },
            axisLine: {
                lineStyle: { color: '#d6dbdb', width: 2 },
            },
        },
        dataZoom: [
            {
                type: 'inside',
            },
            {
                type: 'slider',
                height: 20,
                bottom: 10,
                labelFormatter: (value: number) => getTimezoneFormattedTime(new Date(value), market.timezone),
            },
        ],
        series: [
            {
                name: seriesName(selectedPriceType),
                type: 'line',
                data: chartData,
                showSymbol: false,
                smooth: false,
                color: CHART_DATA_COLOR,
                lineStyle: { width: 2, color: CHART_DATA_COLOR },
                areaStyle: { color: 'rgba(116, 215, 213, 0.35)' },
            },
            ...steppedSeries,
        ],
    };

    return (
        <Row
            justify="space-between"
            gutter={[0, 0]}
            className="content-background"
            style={{
                marginTop: '16px',
                marginBottom: '16px',
                padding: '32px 8px',
                borderRadius: 8,
            }}
        >
            <Col xs={24} md={11}>
                <div className="content-row">
                    <Row align="middle" justify="center">
                        <Tooltip title="Prices based on the selected period">
                            <Space align="center">
                                <MoneyIcon
                                    style={{
                                        width: '40px',
                                        height: '40px',
                                        fill: themeColors.colorPrimary,
                                    }}
                                />
                                <Typography.Title
                                    level={2}
                                    style={{
                                        color: themeColors.colorPrimary,
                                        margin: 0,
                                    }}
                                >
                                    Prices
                                </Typography.Title>
                            </Space>
                        </Tooltip>
                    </Row>
                </div>
            </Col>

            <Col xs={24} md={0}>
                <Divider style={{ height: '100%' }} />
            </Col>

            <Col xs={24} md={11}>
                <Row
                    justify="end"
                    style={{
                        margin: '10px 40px',
                    }}
                >
                    <Segmented options={priceTypeOptions} value={selectedPriceType} onChange={value => setSelectedPriceType(value as PriceType)} />
                </Row>
            </Col>

            <Col span={24}>
                {isNoData ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                ) : (
                    <ReactEChartsCore
                        echarts={echarts}
                        option={option}
                        style={{ height: 400, width: '100%' }}
                        showLoading={isLoading}
                        loadingOption={{ color: 'rgba(116, 215, 213, 0.8)' }}
                        onChartReady={instance => {
                            instance.group = CHART_GROUP_ID;
                            chartInstanceRef.current = instance;
                        }}
                        notMerge
                        lazyUpdate
                    />
                )}
            </Col>
        </Row>
    );
};
